// PropertyAutocomplete — debounced combobox over /dataengine/search/properties.
// Returns mixed project + HDB hits, each tagged with `kind` so the dropdown
// can label them ("Condo" / "HDB"). Used by the buyer-side Add Property modal.

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { apiJson } from "@/lib/api";

export type PropertyHitKind = "project" | "hdb";

export interface PropertyHit {
  kind: PropertyHitKind;
  name: string;
  subtitle?: string;

  // project-only
  slug?: string | null;
  district?: string | null;
  tenure?: string | null;
  property_types?: string[] | null;
  market_segment?: string | null;

  // hdb-only
  block?: string | null;
  street_name?: string | null;
  estate_name?: string | null;
  year_completed?: number | null;

  // shared
  street?: string | null;
  postal?: string | null;
  lat?: number | null;
  lng?: number | null;
  planning_area?: string | null;
}

export function PropertyAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  onSelect: (hit: PropertyHit) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [hits, setHits] = useState<PropertyHit[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();

  // Debounced query — 180ms feels responsive without thrashing the API.
  useEffect(() => {
    const q = value.trim();
    if (q.length < 2) {
      setHits([]);
      setLoading(false);
      return;
    }
    const myId = ++reqIdRef.current;
    setLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const r = await apiJson<{ rows: PropertyHit[] }>(
          `/dataengine/search/properties?q=${encodeURIComponent(q)}&limit=10`,
        );
        if (myId !== reqIdRef.current) return;
        setHits(r.rows ?? []);
        setActive(0);
      } catch {
        if (myId === reqIdRef.current) setHits([]);
      } finally {
        if (myId === reqIdRef.current) setLoading(false);
      }
    }, 180);
    return () => window.clearTimeout(t);
  }, [value]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const showDropdown = open && (loading || hits.length > 0);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          if (value.trim().length >= 2) setOpen(true);
        }}
        onKeyDown={(e) => {
          if (!showDropdown) {
            if (e.key === "ArrowDown" && hits.length > 0) {
              setOpen(true);
              e.preventDefault();
            }
            return;
          }
          if (e.key === "ArrowDown") {
            setActive((a) => Math.min(a + 1, hits.length - 1));
            e.preventDefault();
          } else if (e.key === "ArrowUp") {
            setActive((a) => Math.max(a - 1, 0));
            e.preventDefault();
          } else if (e.key === "Enter") {
            const hit = hits[active];
            if (hit) {
              onSelect(hit);
              setOpen(false);
              e.preventDefault();
            }
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listId}
        aria-autocomplete="list"
        style={{
          width: "100%",
          padding: "10px 12px",
          borderRadius: 8,
          border: "1px solid var(--ag-rule-strong)",
          background: "var(--ag-surface)",
          fontFamily: "var(--ag-font-ui)",
          fontSize: 13.5,
          color: "var(--ag-ink)",
          outline: "none",
        }}
      />
      {showDropdown && (
        <div
          id={listId}
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 20,
            maxHeight: 320,
            overflowY: "auto",
            background: "var(--ag-bg)",
            border: "1px solid var(--ag-rule-strong)",
            borderRadius: 10,
            boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
            padding: 4,
          }}
        >
          {loading && hits.length === 0 && (
            <div
              style={{
                padding: "10px 12px",
                fontSize: 12,
                color: "var(--ag-ink-3)",
              }}
            >
              Searching…
            </div>
          )}
          {hits.map((h, i) => (
            <PropertyHitRow
              key={`${h.kind}:${h.name}:${h.block ?? ""}:${h.street_name ?? ""}`}
              hit={h}
              active={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => {
                onSelect(h);
                setOpen(false);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PropertyHitRow({
  hit,
  active,
  onMouseEnter,
  onClick,
}: {
  hit: PropertyHit;
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  const badge = useMemo(() => kindBadge(hit), [hit]);
  return (
    <button
      type="button"
      onMouseEnter={onMouseEnter}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      role="option"
      aria-selected={active}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "8px 10px",
        borderRadius: 7,
        background: active ? "var(--ag-accent-soft)" : "transparent",
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: "var(--ag-font-ui)",
      }}
    >
      <span
        style={{
          flexShrink: 0,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.5,
          textTransform: "uppercase",
          padding: "3px 7px",
          borderRadius: 999,
          background: badge.bg,
          color: badge.fg,
          border: `1px solid ${badge.border}`,
        }}
      >
        {badge.label}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--ag-ink)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {hit.name}
        </div>
        {hit.subtitle && (
          <div
            style={{
              fontSize: 11.5,
              color: "var(--ag-ink-3)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {hit.subtitle}
          </div>
        )}
      </span>
    </button>
  );
}

function kindBadge(hit: PropertyHit): {
  label: string;
  bg: string;
  fg: string;
  border: string;
} {
  if (hit.kind === "hdb") {
    return {
      label: "HDB",
      bg: "rgba(214, 138, 60, 0.12)",
      fg: "#a55a14",
      border: "rgba(214, 138, 60, 0.35)",
    };
  }
  // For private projects, prefer property_types[0] as a finer-grained badge.
  const t = (hit.property_types?.[0] ?? "").toLowerCase();
  let label = "Condo";
  if (t.includes("apartment")) label = "Apt";
  else if (t.includes("terrace") || t.includes("semi-detached") || t.includes("detached"))
    label = "Landed";
  else if (t.includes("executive condominium")) label = "EC";
  return {
    label,
    bg: "rgba(56, 122, 90, 0.12)",
    fg: "#2f6c4f",
    border: "rgba(56, 122, 90, 0.35)",
  };
}
